<template>
  <div class="hero-body">
    <div class="container has-text-centered">
      <div class="interview-summary">
        <h1 class="title">{{ msg }}</h1>
        <p>
          Review your responses and select
          <em>Submit</em> to confirm.
        </p>

        <div
          class="answer mv2"
          :key="id"
          v-for="(answer, id, index) in answers"
        >
          <div class="answer-content">
            <div class="question mb1">
              Question {{ index + 1 }}
              <div class="is-pulled-right">
                <i
                  class="icon icon-pencil pointer"
                  @click="editQuestion(id)"
                ></i>
              </div>
            </div>
            <h3 class="mb1">{{ allQuestions[id].title }}</h3>

            <div v-if="allQuestions[id].kind === 'file'">
              <div class="columns">
                <div class="column" :key="file.name" v-for="file in answer">
                  <img v-if="$isImage(file)" :src="file.data" width="150" />
                  <i v-if="!$isImage(file)" class="fa fa-file"></i>
                  <p>{{ file.name }}</p>
                </div>
              </div>
            </div>

            <div v-else-if="allQuestions[id].kind === 'stripe'">
              ****{{ answer.card.last4 }}
            </div>

            <div v-else-if="allQuestions[id].kind === 'visuwell_user'">
              <p v-if="answer.action == 'find'">Patient found.</p>
              <p v-else-if="answer.action == 'create'">Patient created.</p>
            </div>

            <div v-else-if="allQuestions[id].kind === 'instruction'">
              <div v-html="instructionText(allQuestions[id])"></div>
              <div class="mt1" v-if="answer.name">
                <h3>Name</h3>
                <p>{{ answer.name }}</p>
              </div>

              <div v-if="answer.signature">
                <hr />
                <h3>Signature</h3>
                <img
                  :src="answer.signature.data"
                  :alt="answer.signature.file"
                />
              </div>
            </div>

            <div v-else-if="isArray(answer) && answer.length">
              <div v-if="isChoice(answer[0])" class="choice">
                <div v-for="choice in answer" :key="choice">
                  {{ choice.value }}
                </div>
              </div>
              <div v-if="!isChoice(answer[0])" class="choice">
                <div :key="choice" v-for="choice in answer">
                  {{ choice }}
                </div>
              </div>
            </div>

            <div v-else-if="isObject(answer)">
              <div class="choice" :key="value" v-for="value in values(answer)">
                {{ value }}
              </div>
            </div>

            <div v-else-if="typeof answer === 'string'">{{ answer }}</div>

            <div v-else>_ _ _ _ _ _ _ _ _ _ _ _ _ _</div>
          </div>
        </div>

        <div class="fixed-button">
          <button
            :class="[
              'button',
              'button-answer',
              'is-primary',
              'is-fullwidth',
              'submit-button',
              { 'is-loading': loading }
            ]"
            @click="sendInterview"
            v-if="allowSubmit()"
          >
            Submit
          </button>
        </div>

        <div v-if="message.length" class="notification is-danger">
          {{ message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import InterviewsService from "@/services/InterviewsService";
import VisuwellService from "../services/VisuwellService";
import PatientQueuesService from "../services/PatientQueuesService";
import { redirectToVisuwell } from "../helpers/visuwell";

export default {
  name: "interview-summary",

  data() {
    return {
      loading: false,
      message: "",
      msg: "Review and Submit:"
    };
  },

  computed: {
    answers() {
      return this.$store.state.interview.answers;
    },
    allQuestions() {
      return this.$store.getters["interview/allQuestions"];
    },
    interviewId() {
      return this.$store.state.interview.id;
    },
    patientQueueId() {
      return this.$store.state.interview.patientQueue.id;
    },
    referrer() {
      return this.$store.getters["interview/getReferrer"];
    },
    patient() {
      return this.$store.getters["interview/getPatient"];
    }
  },

  methods: {
    isArray(answer) {
      return _.isArray(answer);
    },

    isObject(answer) {
      return _.isObject(answer) && !_.isArray(answer);
    },

    values(answer) {
      return _.values(answer);
    },

    isChoice(answer) {
      return _.has(answer, "value");
    },

    instructionText(question) {
      return _.get(question, "fields[0].choice");
    },

    editQuestion(id) {
      this.$store.dispatch("interview/editQuestion", id);
      const p = this.$store.getters["interview/getPage"];
      const q = this.$store.getters["interview/getQuestion"];
      this.$router.push({ name: "interview", params: { p, q } });
    },

    sendInterview() {
      this.loading = true;
      this.message = "";
      const onDemandSession = this.$store.getters[
        "interview/getOnDemandSession"
      ];

      let interviewAlternateEndingId = this.$store.getters[
        "interview/getAlternateEnding"
      ];
      InterviewsService.finish(
        this.interviewId,
        this.patientQueueId,
        this.answers,
        {
          referrer: this.referrer,
          patient: this.patient
        },
        onDemandSession,
        interviewAlternateEndingId
      )
        .then(({submitted, response}) => {
          const onDemandJwt = this.$store.getters["interview/getOnDemandJwt"];

          const onDemandRedirect = this.$store.getters[
            "interview/getOnDemandRedirect"
          ];
          const onDemandAutologin = this.$store.getters[
            "interview/getOnDemandAutologin"
          ];

          if (submitted) {
            if (response && response.data && response.data['redirect_to']) {
                window.location = response.data['redirect_to'];
            } else if (onDemandRedirect != null) {
              if (onDemandAutologin == true) {
                VisuwellService.login(onDemandJwt, onDemandRedirect).then(
                  redirectToVisuwell
                );
              } else {
                window.location = onDemandRedirect;
              }
            } else {
              if (this.hasAlternateEnding()) {
                this.$router.push({ name: "alternate-ending" });
              } else {
                this.$router.push({ name: "success" });
              }
            }
          } else {
            this.loading = false;
            this.message =
              "Sorry, we could not submit your appointment request. Please try again.";
          }
        })
        .catch(error => {
          console.error("InterviewSummary - InterviewsService.finish error=", error)
          this.loading = false;
          this.message = `Sorry, we could not submit your appointment request. ${
            error.response.data.errors[0].detail
          }`;
        });
    },

    allowSubmit() {
      return !this.$store.getters["interview/preview"];
    },

    hasAlternateEnding() {
      let ending = this.$store.getters["interview/getAlternateEnding"];

      return !_.isEmpty(ending);
    }
  },

  mounted() {
    // force the logo to show on the summary page
    window.scrollTo(0, 0);

    if (!this.patientQueueId) {
      let patientQueueId = this.$route.params.id;
      PatientQueuesService.fetch(patientQueueId).catch(error => {
        if (error.response.status == 403) {
          console.error(`Not authorized for ${patientQueueId}`);
          alert("Your session has timed out and you will have to start over.");
          this.$router.push({ name: "home" });
        } else {
          alert("Something went wrong. Please try again.");
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.question {
  color: #ccc;
}

.answer {
  border: 1px solid #ccc;
}
.fa-file {
  font-size: 110px;
  margin-bottom: 10px;
}
</style>
